
import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppUser, Permissions, Store } from '../models/index';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router: Router = inject(Router);
  private userService = inject(UserService);
  private supabase: SupabaseClient | null = getSupabase();
  
  currentUser = signal<AppUser | null>(null);
  currentStore = signal<Store | null>(null);
  permissions = signal<Permissions | null>(null);

  isLoggedIn = computed(() => this.currentUser() !== null);
  
  private isManualLoginInProgress = false;

  hasFeature = (featureKey: string) => computed(() => {
    return this.permissions()?.features?.[featureKey] ?? false;
  });

  getSupabaseClient() { return this.supabase; }

  private _mapProfileToAppUser(profile: any): AppUser | null {
    if (!profile) return null;
    return {
      uid: profile.uid,
      storeId: profile.store_id,
      email: profile.email,
      name: profile.name,
      role: profile.role,
      status: profile.status,
      pin: profile.pin,
      createdAt: profile.createdAt,
      signatureUrl: profile.signature_url, // Added signature mapping
    };
  }

  constructor() {
    const supabase = this.supabase;
    if (supabase) {
      // Clean previous stuck states
      localStorage.removeItem('auth_redirect_in_progress');
      localStorage.removeItem('auth_redirect_ts');

      // 1. Listen for Auth State Changes
      // FIX: Cast to 'any' to bypass a suspected type definition issue in the installed '@supabase/supabase-js' package where methods on the 'auth' client are not being recognized.
      (supabase.auth as any).onAuthStateChange(async (event: any, session: any) => {
        console.log(`[AuthService] Auth State Change: ${event}`);

        if (event === 'PASSWORD_RECOVERY') {
            this.router.navigate(['/update-password']);
        }

        if (session?.user) {
          // If we are already synced with this user, skip
          if (this.currentUser()?.uid === session.user.id) return;
          
          await this.fetchAndSetUser(session.user.id);
        } else {
          // Only clear if we are NOT in the middle of a PIN session (prevent flicker)
          if (!localStorage.getItem('custom_pin_token')) {
            this.currentUser.set(null);
            this.permissions.set(null);
            this.currentStore.set(null);
          }
        }
      });
      
      this.hydratePinSession();
    }
  }

  // Explicitly check session (used by Guards and Init)
  async checkCurrentSession(): Promise<boolean> {
      if (!this.supabase) return false;
      // FIX: Cast to 'any' to bypass a suspected type definition issue in the installed '@supabase/supabase-js' package where methods on the 'auth' client are not being recognized.
      const { data } = await (this.supabase.auth as any).getSession();
      if (data.session?.user) {
          await this.fetchAndSetUser(data.session.user.id);
          return true;
      }
      return false;
  }

  async fetchAndSetUser(uid: string): Promise<boolean> {
    if (!this.supabase) return false;
    
    // Fetch Profile
    const { data: profile, error: profileError } = await this.supabase
      .from('users').select('*').eq('uid', uid).single();

    if (profileError) {
      console.error("[AuthService] User profile missing for ID:", uid);
      // Only force logout if it's a critical error, not network blip
      if (profileError.code === 'PGRST116') { // Not found
         this.logout();
      }
      return false;
    }
    
    if (profile) {
      // 1. Load Store First (Critical for Routing)
      if (profile.store_id) {
          localStorage.setItem('bound_store_id', profile.store_id);
          const { data: store } = await this.supabase.from('stores').select('*').eq('id', profile.store_id).single();
          
          if (store) {
              const mappedStore: Store = {
                  ...store,
                  plan_id: store.plan_id,
                  setupCompleted: store.setup_completed,
                  businessType: store.business_type || 'CLINIC', 
                  // Map JSON settings
                  portal_settings: store.portal_settings,
                  feature_settings: store.feature_settings,
                  receipt_settings: store.receipt_settings,
                  tax_settings: store.tax_settings,
                  loyalty_settings: store.loyalty_settings,
                  clinic_settings: store.clinic_settings
              };
              this.currentStore.set(mappedStore);
          }
      }

      // 2. Set User
      this.currentUser.set(this._mapProfileToAppUser(profile));
      
      // 3. Load Permissions
      await this.loadPermissions();
      return true;
    }
    return false;
  }

  private async hydratePinSession() {
      const token = localStorage.getItem('custom_pin_token');
      if (token && this.supabase) {
          try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              const now = Math.floor(Date.now() / 1000);
              if (payload.exp && payload.exp < now) throw new Error("Token expired");
              
              this.supabase.realtime.setAuth(token);
              
              if (payload.sub) {
                  await this.fetchAndSetUser(payload.sub);
              }
          } catch (e) {
              console.error("[AuthService] PIN Session Invalid:", e);
              this.logout();
          }
      }
  }

  async reloadCurrentUserProfile(): Promise<void> {
    const currentUser = this.currentUser();
    if (!this.supabase || !currentUser) return;
    await this.fetchAndSetUser(currentUser.uid);
  }

  async loadPermissions(): Promise<void> {
      const user = this.currentUser();
      // Platform Admin always gets full access
      if (user?.role === 'platform_admin') {
         this.permissions.set({
             features: {
                 "core_pos": true, "ai_order": true, "telemedicine": true, 
                 "loyalty": true, "reporting": true, "partner_network": true,
                 "nfc_cards": true, "automated_promptpay": true, 
                 "multi_store": true, "compliance_reports": true
             },
             limits: { "users": null, "medical_cases": null }
         });
         return;
      }
      // For all other users (including 'owner'), get permissions from the backend
      try {
        const perms = await this.userService.getUserPermissions();
        this.permissions.set(perms);
      } catch (e) {
        // Fallback to no permissions on error
        this.permissions.set({ features: {}, limits: {} }); 
      }
  }

  async reloadPermissions(): Promise<void> { await this.loadPermissions(); }

  // --- Helper to log login attempts ---
  private async logAttempt(identifier: string, method: 'PASSWORD' | 'GOOGLE' | 'PIN', status: 'SUCCESS' | 'FAILED', reason?: string) {
      if (!this.supabase) return;
      try {
          await this.supabase.rpc('log_login_attempt', {
              p_identifier: identifier,
              p_method: method,
              p_status: status,
              p_error_reason: reason
          });
      } catch (e) {
          console.warn('Failed to log login attempt:', e);
      }
  }

  async login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    if (!this.supabase) return { success: false, error: 'DB not connected.' };
    if (this.isManualLoginInProgress) return { success: false, error: 'Login in progress' };

    this.isManualLoginInProgress = true;
    try {
      localStorage.removeItem('custom_pin_token');
      // FIX: Cast to 'any' to bypass a suspected type definition issue
      const { data, error } = await (this.supabase.auth as any).signInWithPassword({ email, password });
      
      if (error) {
        // Log Failure
        this.logAttempt(email, 'PASSWORD', 'FAILED', error.message);
        throw error;
      }
      
      if (data.user) {
         // Log Success
         this.logAttempt(email, 'PASSWORD', 'SUCCESS');
         return { success: true };
      }
      
      return { success: false, error: 'No user returned.' };
    } catch (e: any) {
        return { success: false, error: e.message || 'Login failed' };
    } finally {
      this.isManualLoginInProgress = false;
    }
  }

  async signup(email: string, password: string, name: string, token: string | null, businessType: 'CLINIC' | 'DISPENSARY', pin?: string): Promise<{ success: boolean; error?: string }> {
    if (!this.supabase) return { success: false, error: 'DB not connected.' };
    const options: any = { data: { name: name, token: token, businessType: businessType } };
    if (pin) options.data.pin = pin;

    // FIX: Cast to 'any' to bypass a suspected type definition issue in the installed '@supabase/supabase-js' package where methods on the 'auth' client are not being recognized.
    const { data, error } = await (this.supabase.auth as any).signUp({ email, password, options });
    if (error) { return { success: false, error: error.message }; }
    return { success: true };
  }

  async loginWithGoogle(): Promise<{ success: boolean; error?: string }> {
    if (!this.supabase) return { success: false, error: 'DB not connected.' };
    localStorage.removeItem('custom_pin_token');
    
    // FIX: Cast to 'any' to bypass a suspected type definition issue in the installed '@supabase/supabase-js' package where methods on the 'auth' client are not being recognized.
    const { data, error } = await (this.supabase.auth as any).signInWithOAuth({
      provider: 'google', 
      options: { redirectTo: window.location.origin }
    });
    
    if (error) return { success: false, error: error.message };
    return { success: true };
  }

  async loginWithPin(pin: string): Promise<{ success: boolean; error?: string }> {
    if (!isSupabaseConfigured() || !this.supabase) return { success: false, error: 'DB not connected.' };
    const boundStoreId = this.getBoundStoreId();
    if (!boundStoreId) return { success: false, error: "Device not setup." };

    this.isManualLoginInProgress = true;
    try {
      const { data: rpcData, error: rpcError } = await this.supabase.rpc('login_with_pin', { 
          pin_to_check: pin,
          store_id_to_check: boundStoreId
      });
      if (rpcError) return { success: false, error: rpcError.message };
      if (!rpcData || !rpcData.session) return { success: false, error: "Invalid PIN." }; 

      const { session } = rpcData;
      if (session && session.access_token) {
          localStorage.setItem('custom_pin_token', session.access_token);
          this.supabase.realtime.setAuth(session.access_token);
          await this.fetchAndSetUser(session.user.id);
          
          // Legacy Log (for audit trail display)
          await this.supabase.rpc('log_client_login_event', {
              p_event_type: 'LOGIN_SUCCESS',
              p_description: `User ${this.currentUser()?.name} logged in via PIN.`
          });

          this.router.navigate(['/admin/customers']);
          return { success: true };
      }
      return { success: false, error: "Login failed." };
    } catch (error: any) {
      return { success: false, error: error.message };
    } finally {
      this.isManualLoginInProgress = false;
    }
  }

  async customerLoginWithPhone(phoneOrCode: string): Promise<{ success: boolean; token?: string; error?: string }> {
    if (!this.supabase) return { success: false, error: 'DB not connected.' };
    const { data, error } = await this.supabase.rpc('customer_login', { p_phone_or_code: phoneOrCode });
    if (error) return { success: false, error: error.message };
    if (!data?.token) return { success: false, error: "No token returned." };
    localStorage.setItem('customer_jwt', data.token);
    return { success: true, token: data.token };
  }

  async logout(): Promise<void> {
    if (this.supabase && this.currentUser()) {
        await this.supabase.rpc('log_client_login_event', {
            p_event_type: 'LOGOUT',
            p_description: `User ${this.currentUser()?.name} logged out.`
        });
    }
    localStorage.removeItem('custom_pin_token');
    // FIX: Cast to 'any' to bypass a suspected type definition issue in the installed '@supabase/supabase-js' package where methods on the 'auth' client are not being recognized.
    if (this.supabase) await (this.supabase.auth as any).signOut();
    this.currentUser.set(null);
    this.permissions.set(null);
    this.currentStore.set(null);
    this.router.navigate(['/']);
  }

  async cleanUpLocalState() {
     localStorage.removeItem('custom_pin_token');
     this.currentUser.set(null);
     this.permissions.set(null);
     this.currentStore.set(null);
     this.isManualLoginInProgress = false;
  }
  
  async prepareForNewSession() {
      await this.cleanUpLocalState();
      // FIX: Cast to 'any' to bypass a suspected type definition issue in the installed '@supabase/supabase-js' package where methods on the 'auth' client are not being recognized.
      if (this.supabase) await (this.supabase.auth as any).signOut();
  }
  
  getBoundStoreId(): string | null {
      return localStorage.getItem('bound_store_id');
  }

  async hardReset() {
      localStorage.clear();
      sessionStorage.clear();
      // FIX: Cast to 'any' to bypass a suspected type definition issue in the installed '@supabase/supabase-js' package where methods on the 'auth' client are not being recognized.
      if (this.supabase) await (this.supabase.auth as any).signOut();
      window.location.reload();
  }

  async sendPasswordResetEmail(email: string): Promise<{ success: boolean; error?: string }> {
      if (!this.supabase) return { success: false, error: 'DB not connected.' };
      // FIX: Cast to 'any' to bypass a suspected type definition issue in the installed '@supabase/supabase-js' package where methods on the 'auth' client are not being recognized.
      const { error } = await (this.supabase.auth as any).resetPasswordForEmail(email, { redirectTo: window.location.origin });
      if (error) return { success: false, error: error.message };
      return { success: true };
  }

  async updateUserPassword(password: string): Promise<{ success: boolean; error?: string }> {
      if (!this.supabase) return { success: false, error: 'DB not connected.' };
      // FIX: Cast to 'any' to bypass a suspected type definition issue in the installed '@supabase/supabase-js' package where methods on the 'auth' client are not being recognized.
      const { error } = await (this.supabase.auth as any).updateUser({ password });
      if (error) return { success: false, error: error.message };
      return { success: true };
  }
}
